import { useState, useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { ErrorCode, errorTitle } from 'common/errors'
import { AppDispatch, GlobalState } from '../../store'
import { LoginDialog, type SsoLoginMethod } from 'common-ui/components/LoginDialog'
import { fetchCurrentUser, getLoginMethods, loginUser } from '../../redux/actions/userActions'
import { LoginLogo } from '../common/LogoFetcher'
import { LoginMethod, SsoStrategy } from 'common/api/v1/types'
import { enqueueSnackbar } from '../../redux/actions/notificationActions'

export const Login = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { loading, loginFail } = useSelector(
    (state: GlobalState) => ({ loading: state.userReducer.loading, loginFail: state.userReducer.loginFail }),
    shallowEqual,
  )
  const isMFARequired = loginFail?.details === errorTitle(ErrorCode.mfaRequired)
  const [ssoLoginMethods, setSsoLoginMethods] = useState<LoginMethod[]>([])

  useEffect(() => {
    const fetchLoginMethods = async () => {
      try {
        const res = await dispatch(getLoginMethods()) // Await the async action dispatch
        const payload = res?.payload as any
        setSsoLoginMethods(Array.isArray(payload) ? payload : [])
      } catch {
        setSsoLoginMethods([])
      }
    }
    fetchLoginMethods()
    handleSsoCallback()
  }, [dispatch])

  const handleLogin = ({ username, password, otp }: { username: string; password: string; otp?: string }) => {
    dispatch(loginUser({ username, password, otp }))
  }

  const handleSsoLogin = (strategy: string) => {
    window.location.href = `/api/sso/${strategy}`
  }

  const handleSsoCallback = () => {
    const params = new URLSearchParams(window.location.search)
    const errorInfo = params.get('ssoerror')
    if (errorInfo) {
      dispatch(
        enqueueSnackbar({
          message: errorInfo,
          type: 'fatalError',
          data: { text: 'SSO Login failed', details: errorInfo, httpStatusCode: 0 },
          options: {
            variant: 'error',
            persist: true,
          },
        }),
      )
      history.pushState({}, '', '/')
    }

    if (params.has('ssologin')) {
      dispatch(fetchCurrentUser())?.then(() => {
        history.pushState({}, '', '/')
      })
      return
    }
  }

  const filteredSsoMethods: SsoLoginMethod[] = ssoLoginMethods
    .filter((m) => Object.values(SsoStrategy).includes(m.strategy as SsoStrategy))
    .map((m) => ({ strategy: m.strategy, displayName: m.displayName ?? m.strategy }))

  return (
    <LoginDialog
      onLogin={handleLogin}
      loading={loading}
      error={loginFail !== undefined && !isMFARequired ? 'Your authentication information is incorrect.' : undefined}
      mfaRequired={isMFARequired}
      ssoLoginMethods={filteredSsoMethods}
      onSsoLogin={handleSsoLogin}
      logo={<LoginLogo />}
    />
  )
}
